import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import type { ParsedTransaction } from "@stepfinance/transaction-history";
import { useQuery } from "@tanstack/react-query";

import SteppyHand from "../../../assets/steppy-ok-hand.svg";
import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { notify } from "../../../utils/notifications";
import { STEP_API_ENDPOINT } from "../../../utils/swr";
import { getTokenByMint } from "../../../utils/tokenInfo";
import { fetchAndCacheMetaplexMetadata, fetchJSONFromUrl } from "../../../utils/utils";
import { useStepAuthStore } from "../../stores/stepAuth";

export const useLatestTransactionHistory = (showFreeTransactions?: boolean) => {
  const {
    currentLanguageData: { common },
  } = useLanguageContext();
  const { connection } = useConnection();
  const { publicKey, watching } = useEnrichedWallet();
  const jwt = useStepAuthStore((s) => s.jwt);

  return useQuery<ParsedTransaction[]>(
    ["transaction-history", publicKey?.toBase58(), watching, jwt],
    async () => fetchTransactionHistoryQuery(connection, publicKey?.toBase58(), watching, jwt),
    {
      retry: 7, // Not to spam lambda after so many fails
      retryDelay: (attemptNumber: number) => {
        if (attemptNumber === 4) {
          notify({
            icon: <img src={SteppyHand} width={40} alt="step-guy" />,
            type: "info",
            message: common.notifications.transactionHistoryLoading,
          });
        }
        // Apply exponential backoff for every retry after 3 retries, max out at 1 minute between retries.
        return Math.min(attemptNumber > 3 ? 2 ** attemptNumber * 1000 : 2500, 60 * 1000);
      },
      enabled: publicKey !== null && showFreeTransactions,
      refetchOnWindowFocus: false,
      onError() {
        notify({ type: "error", message: `${common.notifications.transactionHistoryError.message}`, duration: 20 });
      },
    },
  );
};

const fetchTransactionHistoryQuery = async (
  connection: Connection,
  wallet: string | undefined,
  watching: boolean,
  jwt: string | null,
) => {
  const data: ParsedTransaction[] = await fetchJSONFromUrl(
    `${STEP_API_ENDPOINT}/v1/transaction-history/latest-chain?pubkey=${wallet}`,
    { headers: { Authorization: `bearer ${jwt}` } },
  );

  const unknownMints = new Set<string>();
  for (const tx of data) {
    const sentData = Object.keys(tx.metadata.parsedBalanceChanges.sent);
    for (const key of sentData) {
      if (!getTokenByMint(key)) {
        unknownMints.add(key);
      }
    }

    const receivedData = Object.keys(tx.metadata.parsedBalanceChanges.recieved);
    for (const key of receivedData) {
      if (!getTokenByMint(key)) {
        unknownMints.add(key);
      }
    }
  }

  await fetchAndCacheMetaplexMetadata(
    connection,
    Array.from(unknownMints.values()).map((k) => new PublicKey(k)),
    false,
  );

  return data;
};
